import log from 'loglevel';

import { HierarchicalLabel } from '../models';
import { createApiWithAuth } from '../utils/axios';
import { LabelAndFolderReqType } from '../utils/constants';

export type MessageListVisibility = 'show' | 'hide';
export type LabelListVisibility =
  | 'labelShow'
  | 'labelHide'
  | 'labelShowIfUnread';

let labelApi: ReturnType<typeof createApiWithAuth> | null = null;

const getLabelApi = () => {
  if (!labelApi) {
    const baseUrl = import.meta.env.VITE_GOOGLE_BASE_URL;
    if (!baseUrl) {
      throw new Error('Label base URL is not set');
    }
    labelApi = createApiWithAuth(baseUrl);
  }
  return labelApi;
};

const handleRequestError = (context: string, error: unknown) => {
  log.error(`Label API Error [${context}]:`, error);
  throw error;
};

interface GetLabelMessagesParams {
  labelId: string;
  emailAddress: string;
}

interface DeleteLabelParams {
  labelId: string;
  emailAddress: string;
}

interface UpdateLabelParams {
  labelId: string;
  emailAddress: string;
  name?: string;
  messageListVisibility?: MessageListVisibility;
  labelListVisibility?: LabelListVisibility;
  color?: {
    backgroundColor: string;
    textColor: string;
  };
  signal?: AbortSignal;
}

interface CreateLabelParams {
  name: string;
  emailAddress: string;
  reqType: LabelAndFolderReqType;
  messageListVisibility?: MessageListVisibility;
  labelListVisibility?: LabelListVisibility;
  parentName?: string;
  color?: {
    backgroundColor: string;
    textColor: string;
  };
}

const getUserLabels = async (): Promise<HierarchicalLabel[]> => {
  try {
    const { data } = await getLabelApi().get('/user/labels');

    return data?.message;
  } catch (error) {
    log.error('Error getting labels:', error);
    throw error;
  }
};

const getLabelMessages = async (
  params: GetLabelMessagesParams,
): Promise<unknown> => {
  try {
    const { data } = await getLabelApi().post('/labels/messages', params);

    return data;
  } catch (error) {
    handleRequestError('getLabelMessages', error);
  }
};

const deleteLabel = async (params: DeleteLabelParams): Promise<unknown> => {
  try {
    const { data } = await getLabelApi().post('/labels/delete', params);

    return data;
  } catch (error) {
    handleRequestError('deleteLabel', error);
  }
};

const updateLabel = async (params: UpdateLabelParams): Promise<unknown> => {
  try {
    const { signal, ...requestParams } = params;
    const { data } = await getLabelApi().put('/labels/update', requestParams, {
      signal,
    });

    return data;
  } catch (error) {
    handleRequestError('updateLabel', error);
  }
};

const createLabel = async (params: CreateLabelParams): Promise<any> => {
  try {
    const { data } = await getLabelApi().post('/labels/create', params);

    return data;
  } catch (error) {
    handleRequestError('createLabel', error);
  }
};

export {
  createLabel,
  deleteLabel,
  getLabelMessages,
  getUserLabels,
  updateLabel,
};
